import { computed, ref } from 'vue'
import type {
  UserStateKeyGlobal,
  UserStateValueMapGlobal,
  GetUserStateGlobalResponseMap
} from '@gat-ship/types'
import { DEFAULT_USER_STATE_VALUE_MAP_GLOBAL } from '@gat-ship/types'
import {
  getMultipleItemsRequest,
  removeItemRequest,
  setItemRequest
} from './user-state-api-calls'

export function useUserStateGlobal() {
  const isFetchedKeys = ref<Record<UserStateKeyGlobal, boolean>>({
    theme: false,
    defaultDatabaseId: false
  })

  const state = ref<{
    [K in UserStateKeyGlobal]: UserStateValueMapGlobal[K]
  }>({
    ...DEFAULT_USER_STATE_VALUE_MAP_GLOBAL
  })

  function setState<K extends UserStateKeyGlobal>(
    key: K,
    value: UserStateValueMapGlobal[K]
  ): void {
    state.value[key] = value as UserStateValueMapGlobal[K]
  }

  async function getState<K extends UserStateKeyGlobal>(
    key: K
  ): Promise<UserStateValueMapGlobal[K]> {
    if (!isFetchedKeys.value[key as UserStateKeyGlobal]) {
      await getMultipleItems([key as UserStateKeyGlobal])
    }
    return state.value[key as UserStateKeyGlobal] as UserStateValueMapGlobal[K]
  }

  async function getMultipleItems<
    const K extends readonly UserStateKeyGlobal[]
  >(
    keys: K
  ): Promise<GetUserStateGlobalResponseMap<K, UserStateValueMapGlobal>> {
    const result = await getMultipleItemsRequest({
      body: {
        keys: keys as unknown as string[],
        storageScope: 'global'
      }
    })
    Object.keys(result).forEach((key) => {
      const entry = result[key]
      if (!entry || entry.status !== 'found') {
        return
      }
      isFetchedKeys.value[key as UserStateKeyGlobal] = true
      setState(
        key as UserStateKeyGlobal,
        entry.value as UserStateValueMapGlobal[UserStateKeyGlobal]
      )
    })
    return result as GetUserStateGlobalResponseMap<K, UserStateValueMapGlobal>
  }

  async function setItemGlobal<K extends UserStateKeyGlobal>(
    key: K,
    value: UserStateValueMapGlobal[K]
  ): Promise<void> {
    // Best-effort persistence: see the matching note in
    // user-state-active-db.ts. Failed writes warn and continue rather than
    // bubbling an unhandled rejection through the app.
    try {
      await setItemRequest({
        key,
        queryParams: { storage_scope: 'global' },
        body: value
      })
    } catch (error) {
      console.warn(
        `[user-state] failed to persist global key "${String(key)}" — keeping local value.`,
        error
      )
    }
  }

  async function removeItem<K extends UserStateKeyGlobal>(
    key: K
  ): Promise<void> {
    await removeItemRequest({
      key,
      queryParams: { storage_scope: 'global' }
    })
    isFetchedKeys.value[key] = false
    setState(key, DEFAULT_USER_STATE_VALUE_MAP_GLOBAL[key])
  }
  return {
    state: computed(() => state.value),
    getState,
    getMultipleItems,
    setItemGlobal,
    removeItem
  }
}
